import React from 'react';
import classNames from 'classnames';

import Button from './Button';
import ErrorResponse from './ErrorResponse';
import Spinner, { ComponentProps as SpinnerProps } from './Spinner';

interface ComponentProps {
  /** Set the box's height from the caller's own class (default 12.5rem). */
  className?: string;
  /** The error to show with its Retry button; omit while loading. */
  error?: string | null;
  /** Retry's handler. Retry shows only when this is given. */
  onRetry?: () => void;
  retryText?: string;
  /** Retry's accessible name, when the text alone does not say what is retried. */
  retryLabel?: string;
  /** The spinner's look while loading (type, size, colour); the Spinner's defaults when omitted. */
  spinnerProps?: Omit<SpinnerProps, 'loading' | 'dataCy'>;
  loadingDataCy?: string;
  errorDataCy?: string;
  retryDataCy?: string;
}

/**
 * A box that stands in for a section's content while it loads or after it fails: a spinner, or the error with a
 * Retry button, centred. Styled by container-state in _container-state.scss.
 */
const ContainerState: React.FC<ComponentProps> = ({
  className,
  error,
  onRetry,
  retryText = 'Retry',
  retryLabel,
  spinnerProps,
  loadingDataCy,
  errorDataCy,
  retryDataCy,
}) => {
  if (error != null) {
    return (
      <div className={classNames('container-state', className)}>
        <ErrorResponse message={error} dataCy={errorDataCy} />
        {onRetry && (
          <Button color="info" onClick={onRetry} aria-label={retryLabel} dataCy={retryDataCy}>
            {retryText}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={classNames('container-state', className)} aria-busy="true">
      <Spinner loading dataCy={loadingDataCy} {...spinnerProps} />
    </div>
  );
};

export default ContainerState;
